import React, { useEffect } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";

import schoolLogo from "./tradeschoollogo.jpg";
import defaultAvatar from "./studentava.jpg";

/* =========================================================
   IGNITE SKILL CENTER
   PROFESSIONAL LIGHT STUDENT AUTHENTICATION DOCUMENT
   A4 LANDSCAPE
========================================================= */

const COLORS = {
  orange: "#004d16",
  orangeDark: "#00440c",
  orangeSoft: "#FFF7ED",
  gold: "#bcfb9c",
  goldSoft: "#FFFBEA",

  charcoal: "#1F2937",
  charcoalLight: "#374151",

  text: "#111827",
  muted: "#6B7280",
  mutedLight: "#9CA3AF",

  border: "#E5E7EB",
  borderLight: "#F1F5F9",

  background: "#F8FAFC",
  white: "#FFFFFF",
  cream: "#FFFCF7",

  green: "#15803D",
  greenSoft: "#F0FDF4",

  red: "#B91C1C",
  redSoft: "#FEF2F2",

  blue: "#1D4ED8",
  blueSoft: "#EFF6FF",
};

/* =========================================================
   STYLES
========================================================= */

const styles = StyleSheet.create({
  page: {
    size: "A4",
    backgroundColor: COLORS.background,
    paddingTop: 20,
    paddingBottom: 16,
    paddingHorizontal: 24,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: COLORS.text,
  },

  /* =======================================================
     HEADER
  ======================================================= */

  header: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 10,
    paddingVertical: 11,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 4,
    borderTopColor: COLORS.orange,
    marginBottom: 10,
  },

  brandArea: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  logo: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: COLORS.white,
    borderWidth: 2,
    borderColor: COLORS.orange,
    marginRight: 12,
  },

  brandDetails: {
    flex: 1,
  },

  organizationName: {
    color: COLORS.charcoal,
    fontSize: 16,
    fontWeight: "bold",
    letterSpacing: 0.5,
    marginBottom: 3,
  },

  organizationType: {
    color: COLORS.orangeDark,
    fontSize: 8,
    fontWeight: "bold",
    letterSpacing: 1,
    marginBottom: 4,
  },

  organizationMotto: {
    color: COLORS.muted,
    fontSize: 7.2,
  },

  secureArea: {
    alignItems: "flex-end",
    marginLeft: 15,
  },

  secureBadge: {
    backgroundColor: COLORS.orangeSoft,
    borderWidth: 1,
    borderColor: "#FDBA74",
    borderRadius: 5,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 4,
  },

  secureBadgeText: {
    color: COLORS.orangeDark,
    fontSize: 7.5,
    fontWeight: "bold",
    letterSpacing: 0.8,
  },

  documentNumber: {
    color: COLORS.muted,
    fontSize: 6.8,
  },

  /* =======================================================
     TITLE
  ======================================================= */

  titleArea: {
    backgroundColor: COLORS.cream,
    borderWidth: 1,
    borderColor: "#F3E8D3",
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  titleLeft: {
    flex: 1,
  },

  documentTitle: {
    color: COLORS.charcoal,
    fontSize: 12.5,
    fontWeight: "bold",
    letterSpacing: 0.4,
    marginBottom: 3,
  },

  documentSubtitle: {
    color: COLORS.muted,
    fontSize: 7.2,
    lineHeight: 1.3,
  },

  officialLabel: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.orange,
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 9,
    marginLeft: 12,
  },

  officialLabelText: {
    color: COLORS.orangeDark,
    fontSize: 7,
    fontWeight: "bold",
    letterSpacing: 0.6,
  },

  /* =======================================================
     MAIN CONTENT
  ======================================================= */

  mainContent: {
    flexDirection: "row",
    marginBottom: 9,
  },

  leftColumn: {
    width: "25%",
    paddingRight: 8,
  },

  rightColumn: {
    width: "75%",
    paddingLeft: 2,
  },

  /* =======================================================
     PROFILE CARD
  ======================================================= */

  profileCard: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 9,
    padding: 11,
    alignItems: "center",
    minHeight: 220,
    borderTopWidth: 3,
    borderTopColor: COLORS.orange,
  },

  profileLabel: {
    color: COLORS.orangeDark,
    fontSize: 7,
    fontWeight: "bold",
    letterSpacing: 1,
    marginBottom: 8,
  },

  avatar: {
    width: 88,
    height: 88,
    borderRadius: 44,
    borderWidth: 3,
    borderColor: "#FDBA74",
    backgroundColor: COLORS.background,
    marginBottom: 8,
  },

  studentName: {
    color: COLORS.charcoal,
    fontSize: 11.5,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 1.2,
    marginBottom: 4,
  },

  studentIdLabel: {
    color: COLORS.muted,
    fontSize: 6.8,
    marginBottom: 2,
  },

  studentId: {
    color: COLORS.orangeDark,
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 8,
  },

  verifiedBadge: {
    backgroundColor: COLORS.greenSoft,
    borderWidth: 1,
    borderColor: "#86EFAC",
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 9,
  },

  verifiedText: {
    color: COLORS.green,
    fontSize: 7.2,
    fontWeight: "bold",
    letterSpacing: 0.4,
  },

  /* =======================================================
     SECTIONS
  ======================================================= */

  section: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 8,
  },

  sectionHeader: {
    backgroundColor: "#FFF7ED",
    borderBottomWidth: 1,
    borderBottomColor: "#FED7AA",
    borderLeftWidth: 4,
    borderLeftColor: COLORS.orange,
    paddingVertical: 6,
    paddingHorizontal: 9,
  },

  sectionHeaderText: {
    color: COLORS.charcoal,
    fontSize: 7.8,
    fontWeight: "bold",
    letterSpacing: 0.6,
  },

  sectionBody: {
    paddingVertical: 5,
    paddingHorizontal: 9,
  },

  /* =======================================================
     INFORMATION GRID
  ======================================================= */

  informationGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  informationItem: {
    width: "50%",
    flexDirection: "row",
    paddingVertical: 4,
    paddingRight: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.borderLight,
  },

  label: {
    width: 85,
    color: COLORS.muted,
    fontSize: 6.9,
    fontWeight: "bold",
  },

  value: {
    flex: 1,
    color: COLORS.text,
    fontSize: 7.5,
    fontWeight: "bold",
  },

  /* =======================================================
     PORTAL ACCESS
  ======================================================= */

  portalGrid: {
    flexDirection: "row",
  },

  portalItem: {
    flex: 1,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 5,
    padding: 7,
    marginRight: 7,
  },

  portalItemLast: {
    marginRight: 0,
  },

  portalLabel: {
    color: COLORS.muted,
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 3,
  },

  portalValue: {
    fontSize: 7.8,
    fontWeight: "bold",
  },

  username: {
    color: COLORS.blue,
  },

  password: {
    color: COLORS.red,
  },

  activeStatus: {
    color: COLORS.green,
  },

  /* =======================================================
     SECURITY MESSAGE
  ======================================================= */

  securityMessage: {
    backgroundColor: COLORS.orangeSoft,
    borderWidth: 1,
    borderColor: "#FED7AA",
    borderRadius: 6,
    padding: 7,
    marginTop: 7,
  },

  securityMessageTitle: {
    color: COLORS.orangeDark,
    fontSize: 7.2,
    fontWeight: "bold",
    marginBottom: 3,
  },

  securityMessageText: {
    color: COLORS.charcoalLight,
    fontSize: 6.8,
    lineHeight: 1.35,
  },

  /* =======================================================
     LOWER AREA
  ======================================================= */

  lowerArea: {
    flexDirection: "row",
    marginBottom: 8,
  },

  securityBox: {
    width: "64%",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    padding: 8,
    marginRight: 8,
  },

  securityBoxTitle: {
    color: COLORS.charcoal,
    fontSize: 7.8,
    fontWeight: "bold",
    marginBottom: 4,
  },

  securityItem: {
    color: COLORS.muted,
    fontSize: 6.5,
    lineHeight: 1.3,
    marginBottom: 2,
  },

  authenticationBox: {
    width: "36%",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    padding: 8,
  },

  authenticationTitle: {
    color: COLORS.charcoal,
    fontSize: 7.8,
    fontWeight: "bold",
    marginBottom: 6,
  },

  authenticationContent: {
    flexDirection: "row",
    alignItems: "center",
  },

  verificationBox: {
    width: 60,
    height: 50,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: COLORS.gray2,
    borderRadius: 5,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 9,
  },

  verificationText: {
    color: COLORS.muted,
    fontSize: 6,
    textAlign: "center",
    lineHeight: 1.3,
  },

  signatureArea: {
    flex: 1,
  },

  signatureTitle: {
    color: COLORS.muted,
    fontSize: 6.5,
    marginBottom: 10,
  },

  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.charcoal,
    width: "100%",
    marginBottom: 4,
  },

  signatureRole: {
    color: COLORS.muted,
    fontSize: 6.3,
  },

  /* =======================================================
     FOOTER
  ======================================================= */

  footer: {
    backgroundColor: COLORS.white,
    borderTopWidth: 2,
    borderTopColor: COLORS.orange,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 9,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    color: COLORS.charcoal,
    fontSize: 6.3,
    fontWeight: "bold",
  },

  footerRight: {
    color: COLORS.muted,
    fontSize: 6.2,
    textAlign: "right",
  },
});

/* =========================================================
   PDF DOCUMENT
========================================================= */

const StudentAuthenticationPDF = ({ student }) => {
  const profileImage =
    student?.profile_image ||
    student?.student_admission_image ||
    defaultAvatar;

  const studentInformation = [
    ["Full Name", student?.full_name],
    ["Student ID", student?.admission_id],
    ["Gender", student?.gender],
    ["Program / Class", student?.grade_class_name],
    ["Training Year", student?.current_academic_year],
    ["Date of Birth", student?.date_of_birth],
    ["Parent / Guardian", student?.mother_name],
    ["Contact Number", student?.parent_contact],
  ];

  return (
    <Document
      title={`Ignite Skill Center - Student Authentication - ${
        student?.full_name || "Student"
      }`}
      author="Ignite Skill Center"
      subject="Official Student Authentication and Portal Access Document"
      creator="Ignite Skill Center Management System"
    >
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap={false}
      >
        {/* =================================================
            BRAND HEADER
        ================================================= */}

        <View style={styles.header}>
          <View style={styles.brandArea}>
            <Image
              src={schoolLogo}
              style={styles.logo}
            />

            <View style={styles.brandDetails}>
              <Text style={styles.organizationName}>
                IGNITE SKILL CENTER
              </Text>

              <Text style={styles.organizationType}>
                SKILLS • TRAINING • ENTREPRENEURSHIP
              </Text>

              <Text style={styles.organizationMotto}>
                Empowering people with practical skills for work,
                enterprise, and sustainable opportunity.
              </Text>
            </View>
          </View>

          <View style={styles.secureArea}>
            <View style={styles.secureBadge}>
              <Text style={styles.secureBadgeText}>
                SECURE CREDENTIAL
              </Text>
            </View>

            <Text style={styles.documentNumber}>
              Official Student Identity & Access Record
            </Text>
          </View>
        </View>

        {/* =================================================
            DOCUMENT TITLE
        ================================================= */}

        <View style={styles.titleArea}>
          <View style={styles.titleLeft}>
            <Text style={styles.documentTitle}>
              STUDENT AUTHENTICATION & PORTAL ACCESS DOCUMENT
            </Text>

            <Text style={styles.documentSubtitle}>
              This credential confirms the identity, registration,
              training information, and authorized portal access of
              the student identified below.
            </Text>
          </View>

          <View style={styles.officialLabel}>
            <Text style={styles.officialLabelText}>
              OFFICIAL RECORD
            </Text>
          </View>
        </View>

        {/* =================================================
            MAIN CONTENT
        ================================================= */}

        <View style={styles.mainContent}>
          {/* STUDENT PROFILE */}

          <View style={styles.leftColumn}>
            <View style={styles.profileCard}>
              <Text style={styles.profileLabel}>
                STUDENT PROFILE
              </Text>

              <Image
                src={profileImage}
                style={styles.avatar}
              />

              <Text style={styles.studentName}>
                {student?.full_name || "N/A"}
              </Text>

              <Text style={styles.studentIdLabel}>
                STUDENT IDENTIFICATION
              </Text>

              <Text style={styles.studentId}>
                {student?.admission_id || "N/A"}
              </Text>

              <View style={styles.verifiedBadge}>
                <Text style={styles.verifiedText}>
                  ✓ VERIFIED STUDENT
                </Text>
              </View>
            </View>
          </View>

          {/* RIGHT CONTENT */}

          <View style={styles.rightColumn}>
            {/* STUDENT INFORMATION */}

            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>
                  STUDENT REGISTRATION INFORMATION
                </Text>
              </View>

              <View style={styles.sectionBody}>
                <View style={styles.informationGrid}>
                  {studentInformation.map(
                    ([label, value], index) => (
                      <View
                        key={`${label}-${index}`}
                        style={styles.informationItem}
                      >
                        <Text style={styles.label}>
                          {label}
                        </Text>

                        <Text style={styles.value}>
                          {value || "N/A"}
                        </Text>
                      </View>
                    )
                  )}
                </View>
              </View>
            </View>

            {/* PORTAL ACCESS */}

            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>
                  STUDENT PORTAL ACCESS
                </Text>
              </View>

              <View style={styles.sectionBody}>
                <View style={styles.portalGrid}>
                  <View style={styles.portalItem}>
                    <Text style={styles.portalLabel}>
                      LOGIN EMAIL / USERNAME
                    </Text>

                    <Text
                      style={[
                        styles.portalValue,
                        styles.username,
                      ]}
                    >
                      {student?.email ||
                        student?.admission_id ||
                        "N/A"}
                    </Text>
                  </View>

                  <View style={styles.portalItem}>
                    <Text style={styles.portalLabel}>
                      INITIAL PASSWORD
                    </Text>

                    <Text
                      style={[
                        styles.portalValue,
                        styles.password,
                      ]}
                    >
                      {student?.initial_password ||
                        student?.password ||
                        "N/A"}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.portalItem,
                      styles.portalItemLast,
                    ]}
                  >
                    <Text style={styles.portalLabel}>
                      ACCOUNT STATUS
                    </Text>

                    <Text
                      style={[
                        styles.portalValue,
                        styles.activeStatus,
                      ]}
                    >
                      ACTIVE
                    </Text>
                  </View>
                </View>

                <View style={styles.securityMessage}>
                  <Text style={styles.securityMessageTitle}>
                    IMPORTANT ACCOUNT SECURITY
                  </Text>

                  <Text style={styles.securityMessageText}>
                    Your login credentials are personal and confidential.
                    Do not share your password with another person.
                    Change the initial password immediately after your
                    first successful login and contact Ignite Skill Center
                    administration if your account security is compromised.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* =================================================
            SECURITY + AUTHENTICATION
        ================================================= */}

        <View style={styles.lowerArea}>
          <View style={styles.securityBox}>
            <Text style={styles.securityBoxTitle}>
              DOCUMENT SECURITY & VERIFICATION
            </Text>

            <Text style={styles.securityItem}>
              • This document is generated from the official Ignite
              Skill Center student management system.
            </Text>

            <Text style={styles.securityItem}>
              • Student information must correspond with the official
              registration records maintained by the institution.
            </Text>

            <Text style={styles.securityItem}>
              • Unauthorized alteration, reproduction, or falsification
              of this document may invalidate its use.
            </Text>

            <Text style={styles.securityItem}>
              • The photograph and identification information should
              match the student presenting this credential.
            </Text>

            <Text style={styles.securityItem}>
              • Verification should be performed through authorized
              Ignite Skill Center administration.
            </Text>
          </View>

          <View style={styles.authenticationBox}>
            <Text style={styles.authenticationTitle}>
              OFFICIAL AUTHENTICATION
            </Text>

            <View style={styles.authenticationContent}>
              <View style={styles.verificationBox}>
                <Text style={styles.verificationText}>
                  QR / DIGITAL{"\n"}
                  VERIFICATION{"\n"}
                  AREA
                </Text>
              </View>

              <View style={styles.signatureArea}>
                <Text style={styles.signatureTitle}>
                  Authorized Institutional Signature
                </Text>

                <View style={styles.signatureLine} />

                <Text style={styles.signatureRole}>
                  Authorized Officer / Administrator
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* =================================================
            FOOTER
        ================================================= */}

        <View style={styles.footer}>
          <Text style={styles.footerLeft}>
            © {new Date().getFullYear()} Ignite Skill Center •
            Official Student Credential
          </Text>

          <Text style={styles.footerRight}>
            CONFIDENTIAL INSTITUTIONAL RECORD •
            Generated by Ignite Skill Center Management System
          </Text>
        </View>
      </Page>
    </Document>
  );
};

/* =========================================================
   AUTOMATIC PDF GENERATOR
========================================================= */

const Secure = ({ student }) => {
  useEffect(() => {
    if (!student) return;

    const generatePDF = async () => {
      try {
        const blob = await pdf(
          <StudentAuthenticationPDF student={student} />
        ).toBlob();

        const fileName =
          `Ignite_Skill_Center_Student_Authentication_${
            student?.student_id ||
            student?.admission_id ||
            "Student"
          }.pdf`;

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      } catch (error) {
        console.error(
          "Ignite Skill Center PDF Generation Error:",
          error
        );
      }
    };

    generatePDF();
  }, [student]);

  return null;
};

export default Secure;

